import { useEffect, useState } from 'react';

export interface TrafficSourceData {
  traffic_source: string;
  traffic_medium: string;
  traffic_campaign: string;
  traffic_landing_page: string;
  traffic_referrer: string;
  traffic_utm_source: string;
  traffic_utm_medium: string;
  traffic_utm_campaign: string;
  traffic_utm_term: string;
  traffic_utm_content: string;
  device_type: string;
  browser: string;
  os: string;
}

const STORAGE_KEY = 'sule_traffic_source';

/**
 * Detect device type from user agent
 */
function detectDeviceType(): string {
  if (typeof window === 'undefined') return 'desktop';
  
  const ua = navigator.userAgent.toLowerCase();
  const width = window.innerWidth;
  
  if (/android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
    return 'mobile';
  }
  if (/ipad|tablet|playbook|silk/i.test(ua) || (width >= 768 && width <= 1024)) {
    return 'tablet';
  }
  if (width < 768) return 'mobile';
  
  return 'desktop';
}

/**
 * Detect browser from user agent
 */
function detectBrowser(): string {
  if (typeof window === 'undefined') return 'unknown';
  
  const ua = navigator.userAgent;
  
  if (ua.includes('Chrome') && !ua.includes('Chromium') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  
  return 'Other';
}

/**
 * Detect OS from user agent
 */
function detectOS(): string {
  if (typeof window === 'undefined') return 'unknown';
  
  const ua = navigator.userAgent;
  
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  
  return 'Other';
}

/**
 * Detect traffic source and medium from referrer URL
 */
function detectSourceFromReferrer(referrer: string): { source: string; medium: string } {
  if (!referrer) {
    return { source: 'direct', medium: 'direct' };
  }

  const referrerLower = referrer.toLowerCase();
  const url = new URL(window.location.href);
  const gclid = url.searchParams.get('gclid');
  const fbclid = url.searchParams.get('fbclid');

  // Google - check for paid vs organic
  if (referrerLower.includes('google.')) {
    if (gclid) {
      return { source: 'google', medium: 'paid' };
    }
    return { source: 'google', medium: 'organic' };
  }

  // Bing
  if (referrerLower.includes('bing.com')) {
    return { source: 'bing', medium: 'organic' };
  }

  // Instagram
  if (referrerLower.includes('instagram.com') || referrerLower.includes('l.instagram.com')) {
    return { source: 'instagram', medium: 'social' };
  }

  // Facebook
  if (referrerLower.includes('facebook.com') || referrerLower.includes('fb.com') || referrerLower.includes('l.facebook.com') || fbclid) {
    return { source: 'facebook', medium: 'social' };
  }

  // ChatGPT / OpenAI
  if (referrerLower.includes('chat.openai.com') || referrerLower.includes('chatgpt.com') || referrerLower.includes('openai.com')) {
    return { source: 'chatgpt', medium: 'chatbot' };
  }

  // Perplexity AI
  if (referrerLower.includes('perplexity.ai')) {
    return { source: 'perplexity', medium: 'chatbot' };
  }

  // Claude AI
  if (referrerLower.includes('claude.ai') || referrerLower.includes('anthropic.com')) {
    return { source: 'claude', medium: 'chatbot' };
  }

  // Google Gemini
  if (referrerLower.includes('gemini.google.com') || referrerLower.includes('bard.google.com')) {
    return { source: 'gemini', medium: 'chatbot' };
  }

  // Microsoft Copilot
  if (referrerLower.includes('copilot.microsoft.com')) {
    return { source: 'copilot', medium: 'chatbot' };
  }

  // YouTube
  if (referrerLower.includes('youtube.com') || referrerLower.includes('youtu.be')) {
    return { source: 'youtube', medium: 'social' };
  }

  // Twitter/X
  if (referrerLower.includes('twitter.com') || referrerLower.includes('t.co') || referrerLower.includes('x.com')) {
    return { source: 'twitter', medium: 'social' };
  }

  // LinkedIn
  if (referrerLower.includes('linkedin.com')) {
    return { source: 'linkedin', medium: 'social' };
  }

  // TikTok
  if (referrerLower.includes('tiktok.com')) {
    return { source: 'tiktok', medium: 'social' };
  }

  // Email services
  if (referrerLower.includes('mail.google.com') || referrerLower.includes('outlook.') || referrerLower.includes('mail.yahoo.')) {
    return { source: 'email', medium: 'email' };
  }

  // Default: referral from other sites
  try {
    const referrerHostname = new URL(referrer).hostname;
    return { source: referrerHostname, medium: 'referral' };
  } catch {
    return { source: 'referral', medium: 'referral' };
  }
}

/**
 * Capture and store traffic source data on first page visit
 */
function captureTrafficSource(): TrafficSourceData {
  const url = new URL(window.location.href);
  const referrer = document.referrer || '';

  // Get UTM parameters
  const utmSource = url.searchParams.get('utm_source') || '';
  const utmMedium = url.searchParams.get('utm_medium') || '';
  const utmCampaign = url.searchParams.get('utm_campaign') || '';
  const utmTerm = url.searchParams.get('utm_term') || '';
  const utmContent = url.searchParams.get('utm_content') || '';

  // Determine source and medium
  let source: string;
  let medium: string;

  if (utmSource) {
    // If UTM parameters exist, use them
    source = utmSource.toLowerCase();
    medium = utmMedium.toLowerCase() || 'unknown';
  } else {
    // Otherwise detect from referrer
    const detected = detectSourceFromReferrer(referrer);
    source = detected.source;
    medium = detected.medium;
  }

  return {
    traffic_source: source,
    traffic_medium: medium,
    traffic_campaign: utmCampaign,
    traffic_landing_page: window.location.href,
    traffic_referrer: referrer,
    traffic_utm_source: utmSource,
    traffic_utm_medium: utmMedium,
    traffic_utm_campaign: utmCampaign,
    traffic_utm_term: utmTerm,
    traffic_utm_content: utmContent,
    device_type: detectDeviceType(),
    browser: detectBrowser(),
    os: detectOS(),
  };
}

/**
 * Get stored traffic source or capture new one
 */
function getOrCaptureTrafficSource(): TrafficSourceData {
  try {
    // Check if we already have stored data
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }

    // Capture new traffic source data
    const data = captureTrafficSource();

    // Store in sessionStorage (persists for the session/tab)
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));

    return data;
  } catch {
    // Fallback if storage fails
    return {
      traffic_source: 'unknown',
      traffic_medium: 'unknown',
      traffic_campaign: '',
      traffic_landing_page: typeof window !== 'undefined' ? window.location.href : '',
      traffic_referrer: '',
      traffic_utm_source: '',
      traffic_utm_medium: '',
      traffic_utm_campaign: '',
      traffic_utm_term: '',
      traffic_utm_content: '',
      device_type: detectDeviceType(),
      browser: detectBrowser(),
      os: detectOS(),
    };
  }
}

/**
 * Hook to access traffic source data
 * Captures source on first page load and persists through the session
 */
export function useTrafficSource(): TrafficSourceData {
  const [trafficData, setTrafficData] = useState<TrafficSourceData>(() => {
    // Initialize from storage if available (SSR safe)
    if (typeof window === 'undefined') {
      return {
        traffic_source: '',
        traffic_medium: '',
        traffic_campaign: '',
        traffic_landing_page: '',
        traffic_referrer: '',
        traffic_utm_source: '',
        traffic_utm_medium: '',
        traffic_utm_campaign: '',
        traffic_utm_term: '',
        traffic_utm_content: '',
        device_type: 'desktop',
        browser: 'unknown',
        os: 'unknown',
      };
    }
    return getOrCaptureTrafficSource();
  });

  useEffect(() => {
    // Ensure we capture on mount (handles SSR hydration)
    const data = getOrCaptureTrafficSource();
    setTrafficData(data);
  }, []);

  return trafficData;
}

export default useTrafficSource;
